import React from "react";
import fixture from "../data/gamesAlphaGtaVFixture.json";

const card: React.CSSProperties = {
  border: "1px solid rgba(246,240,230,0.16)",
  borderRadius: 18,
  background: "rgba(246,240,230,0.045)",
  padding: "1.15rem",
};

type PassageHandoff = {
  status: "awaiting_acceptance" | "accepted" | "expired";
  passage_id: string;
  kind: "gifted" | "transferred";
  expires_at: string;
  accepted_at: string | null;
  meaning: string;
  return_path: string;
  receiving_keeper?: {
    status: "received";
    received_at: string;
    receipt_digest: string;
  };
};

function StatusPill({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        border: "1px solid rgba(246,240,230,0.22)",
        borderRadius: 999,
        padding: "0.35rem 0.65rem",
        fontSize: "0.78rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function PassageHandoffCard({ passage }: { passage: PassageHandoff }) {
  if (passage.status === "accepted") {
    return (
      <section style={{ marginTop: "1rem", ...card }} data-testid="xo-games-passage-handoff">
        <StatusPill>Passage accepted</StatusPill>
        <h3>Received by the next Keeper</h3>
        <p style={{ lineHeight: 1.6, opacity: 0.78 }}>
          The receiving-Keeper handoff is recorded through the existing Passage Ceremony. Earlier continuity remains intact.
        </p>
        {passage.receiving_keeper ? (
          <p style={{ fontSize: "0.86rem", opacity: 0.62 }}>
            Receiving receipt: <code>{passage.receiving_keeper.receipt_digest}</code>
          </p>
        ) : null}
        <a href={passage.return_path}>Return to this game</a>
      </section>
    );
  }

  if (passage.status === "expired") {
    return (
      <section style={{ marginTop: "1rem", ...card }} data-testid="xo-games-passage-handoff">
        <StatusPill>Passage expired</StatusPill>
        <h3>No handoff occurred</h3>
        <p style={{ marginBottom: 0, lineHeight: 1.6, opacity: 0.72 }}>
          This invitation expired without acceptance. XO does not upgrade the artifact lifecycle state.
        </p>
      </section>
    );
  }

  return (
    <section style={{ marginTop: "1rem", ...card }} data-testid="xo-games-passage-handoff">
      <StatusPill>Awaiting acceptance</StatusPill>
      <h3>Passage invitation is with the receiving Keeper</h3>
      <p style={{ lineHeight: 1.6, opacity: 0.78 }}>
        Acceptance happens through the private one-time Passage invitation. Its token is never exposed on this public game surface.
      </p>
      <p style={{ marginBottom: 0, fontSize: "0.86rem", opacity: 0.62 }}>{passage.meaning}</p>
    </section>
  );
}

export default function XOGamesAlphaPage({ passage = null }: { passage?: PassageHandoff | null }) {
  const { artifact, evidence, rights, offering, lifecycle } = fixture;

  return (
    <main
      data-testid="xo-games-alpha"
      style={{
        width: "min(980px, calc(100% - 2rem))",
        margin: "0 auto",
        padding: "clamp(2.5rem, 7vw, 6rem) 0",
        color: "#F6F0E6",
      }}
    >
      <header style={{ marginBottom: "2rem" }}>
        <div
          style={{
            fontSize: "0.78rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            opacity: 0.64,
            marginBottom: "0.8rem",
          }}
        >
          xo.games · Alpha
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: "clamp(2.4rem, 7vw, 5.4rem)",
            fontWeight: 500,
            lineHeight: 0.98,
            letterSpacing: "-0.045em",
          }}
        >
          A second life for games.
        </h1>
        <p style={{ maxWidth: 650, fontSize: "1.05rem", lineHeight: 1.65, opacity: 0.76 }}>
          One physical copy. Observable evidence. A continuity record that can follow the copy when it passes to its next Keeper.
        </p>
      </header>

      <section style={{ ...card, padding: "clamp(1.2rem, 4vw, 2rem)", marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
          <StatusPill>{artifact.platform}</StatusPill>
          <StatusPill>Physical copy only</StatusPill>
          <StatusPill>Function: unverified</StatusPill>
        </div>
        <div style={{ fontSize: "0.8rem", opacity: 0.55, marginBottom: "0.4rem" }}>First Second Life acceptance fixture</div>
        <h2 style={{ margin: 0, fontSize: "clamp(2rem, 5vw, 3.6rem)", fontWeight: 500 }}>{artifact.title}</h2>
        <div style={{ marginTop: "0.65rem", fontFamily: "monospace", fontSize: "0.8rem", opacity: 0.5 }}>
          {artifact.artifact_ref}
        </div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
        <section style={card}>
          <h3 style={{ marginTop: 0 }}>What is observed</h3>
          <ul style={{ margin: 0, paddingLeft: "1.15rem", lineHeight: 1.65, opacity: 0.82 }}>
            {evidence.observations.map((observation) => (
              <li key={observation} style={{ marginBottom: "0.55rem" }}>{observation}</li>
            ))}
          </ul>
          <p style={{ marginBottom: 0, marginTop: "1rem", fontSize: "0.86rem", opacity: 0.62 }}>
            These are observations, not a professional grade.
          </p>
        </section>

        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Verification state</h3>
          <dl style={{ margin: 0, display: "grid", gap: "0.85rem" }}>
            <div>
              <dt style={{ fontSize: "0.78rem", opacity: 0.55 }}>FUNCTIONAL STATUS</dt>
              <dd style={{ margin: "0.2rem 0 0" }}>{evidence.functional_status}</dd>
            </div>
            <div>
              <dt style={{ fontSize: "0.78rem", opacity: 0.55 }}>EVIDENCE BINDING</dt>
              <dd style={{ margin: "0.2rem 0 0" }}>Original photo digests pending</dd>
            </div>
            <div>
              <dt style={{ fontSize: "0.78rem", opacity: 0.55 }}>CONTINUITY ROOT</dt>
              <dd style={{ margin: "0.2rem 0 0" }}>Pending original evidence binding</dd>
            </div>
          </dl>
        </section>

        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Rights boundary</h3>
          <p style={{ lineHeight: 1.65, opacity: 0.82 }}>{rights.meaning}</p>
          <div style={{ fontFamily: "monospace", fontSize: "0.78rem", opacity: 0.56 }}>{rights.scope}</div>
        </section>

        <section style={card}>
          <h3 style={{ marginTop: 0 }}>Second Life state</h3>
          <p style={{ margin: "0 0 0.55rem", fontSize: "1.25rem" }}>
            {offering ? "Seeking next Keeper" : "Currently held"}
          </p>
          <p style={{ margin: 0, lineHeight: 1.6, opacity: 0.7 }}>
            {offering
              ? `Fixed offering: ${(offering.price_minor / 100).toFixed(2)} ${offering.currency}.`
              : "No open offering is represented in this Alpha acceptance snapshot."}
          </p>
          <p style={{ margin: "1rem 0 0", fontSize: "0.86rem", opacity: 0.58 }}>
            Recorded transfers: {lifecycle.transfer_count}
          </p>
        </section>
      </div>

      {passage ? <PassageHandoffCard passage={passage} /> : null}

      <footer style={{ marginTop: "1rem", ...card }}>
        <strong>Alpha truth boundary</strong>
        <p style={{ marginBottom: 0, lineHeight: 1.6, opacity: 0.7 }}>
          This development surface renders a bounded GTA V acceptance snapshot. Synthetic test evidence and roots are not presented as real-world evidence. Passage state is shown only when supplied by the existing Passage Ceremony; private invitation tokens and payment details are never exposed here. It does not claim game functionality, grading, digital entitlement, or a broader live marketplace.
        </p>
      </footer>
    </main>
  );
}
